import { logger } from 'features/logger/logger';
import { selectParticipationTranslationManifests } from 'features/participations/store/participationSelectors';
import { getAssetUrlWithTempLocationSupport } from 'features/questionnaires/store/questionnaireAssetApiSlice';
import { TranslationType } from './constants';
import { fetchTranslationFile } from './fetchTranslationFile';
import type { DbProtocolManifest } from '@hub/common';
import type { Store } from '@reduxjs/toolkit';
import type { BackendModule, ResourceLanguage } from 'i18next';

export class DynamicBackend implements BackendModule {
  type = 'backend' as const;
  static type = 'backend' as const;
  static storeInstance: Store | undefined;

  init() {
    logger.info('Initializing i18n DynamicBackend');
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises -- TODO: fix after eslint 9
  async read(language: string, namespace: string): Promise<ResourceLanguage> {
    if (!DynamicBackend.storeInstance) {
      throw new Error(
        `[ASSERT FAILED, I18N] Attempt to rely on storeInstance in DynamicBackend.read while it is not available`
      );
    }

    const translationManifests = selectParticipationTranslationManifests(
      DynamicBackend.storeInstance.getState() as Parameters<
        typeof selectParticipationTranslationManifests
      >[0]
    );

    const [type, ...rest] = namespace.split('-');
    const ns = rest.join('-');
    // just in case
    if (!type || !ns) {
      logger.error(`Invalid namespace: ${namespace}`);
      return {};
    }

    switch (type) {
      case TranslationType.asset:
        return this.loadFile(language, ns, translationManifests);
      default:
        logger.error(`Unknown namespace: ${namespace}`);
        return {}; // unknown namespace - do nothing. Required for the Render PROSE to work.
    }
  }

  private async loadFile(
    lng: string,
    ns: string,
    translationManifests: Record<string, DbProtocolManifest>
  ): Promise<ResourceLanguage> {
    const urlFromManifest = translationManifests[ns]?.translations[lng];

    if (!urlFromManifest) {
      // A namespace can outlive its manifest (e.g. a stale per-participation
      // namespace after a participation switch). Resolve to an empty bundle so
      // i18next records the namespace as loaded rather than looping through
      // failedLoading/backoff retries.
      logger.warn(
        `No manifest entry for namespace ${ns} (${lng}); skipping load`
      );
      return {};
    }

    return await fetchTranslationFile(
      getAssetUrlWithTempLocationSupport(urlFromManifest)
    ).then(data => data.json() as Promise<ResourceLanguage>);
  }
}
